import type {
    ButtonBlockTriggerEvent,
    ButtonItem,
    ButtonSizeType,
    CursorProtocol,
    RichTextContentProtocol,
    VariableADTvalue
} from '@lighthouse/core'
import { ActionItemPattern, ButtonSize, EVENT_TYPE } from '@lighthouse/core'
import React, { useCallback, useMemo } from 'react'

import { btnStyleMap } from '../../constants'
import { useApplicationContext } from '../../contexts'
import { fieldConvertValue, getBackgroundStyle, getBorderStyle, getCursorStyle, getTextColorStyle } from '../../utils'
import { applyFillPickerValue4CssColorValue, useFillPickerContext } from '../FillPicker'
import { useFlowLayoutContext } from '../LayoutEngine'
import { TooltipText } from '../TooltipText'
import { btnTypeShowMap, buttonIconSizeMap, buttonTextSizeMap } from './constant'
import * as SC from './styles'
// import { generateText } from '../RichText'

// generateText()

type ActionButtonStyles = 'title' | 'icon' | 'container' | 'wrapper'
interface ActionButtonProps {
    config: Omit<ButtonItem, 'id'>
    radius?: string
    size?: ButtonSizeType
    cursor?: CursorProtocol
    loading?: boolean
    isSubmit?: boolean
    disabled?: boolean
    styles?: Partial<Record<ActionButtonStyles, React.CSSProperties>>
    onMouseOver?: React.MouseEventHandler<HTMLButtonElement>
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    onRenderTitle: (v: RichTextContentProtocol) => string
    onTriggerEvent?: (name: string) => void
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
    (
        {
            loading,
            isSubmit,
            config,
            styles,
            radius = '0',
            cursor,
            disabled = false,
            size = ButtonSize.sm,
            onMouseOver,
            onClick,
            onRenderTitle,
            onTriggerEvent,
            ...rest
        },
        ref
    ) => {
        const {
            showType = 'icon-name',
            name,
            icon,
            pattern = ActionItemPattern.primary,
            iconPosition = 'left',
            backgroundColor,
            iconColor,
            color,
            borderColor,
            fillWay = 'auto',
            eventList
        } = config
        const triggerEvents = useMemo(() => {
            return eventList?.reduce<ButtonBlockTriggerEvent[]>((acc, ev) => {
                if (!acc.includes('click') && ev.type === EVENT_TYPE.CLICK) {
                    acc.push('click')
                }
                if (!acc.includes('hover') && (ev.type === EVENT_TYPE.MOUSE_ENTER || ev.type === EVENT_TYPE.MOUSE_LEAVE)) {
                    acc.push('hover')
                }
                return acc
            }, [])
        }, [eventList])
        const { appId = '' } = useApplicationContext()
        const { icon: showIcon, name: showName } = useMemo(() => btnTypeShowMap.get(showType) ?? { icon: true, name: true }, [showType])

        const title = useMemo(() => onRenderTitle(name), [name, onRenderTitle])
        const { palettes } = useFillPickerContext()

        const isCustom = pattern === ActionItemPattern.custom
        const patternStyle = btnStyleMap.get(pattern) ?? {}

        const { useParseVariableWithSubscription } = useFlowLayoutContext()
        const parseBackgroundColorVariable = useParseVariableWithSubscription(backgroundColor?.variable)

        const style: React.CSSProperties = useMemo(() => {
            const flex = fillWay === 'auto' ? undefined : { flex: 1 }
            return {
                ...styles?.container,
                ...flex,
                ...(isCustom
                    ? getBackgroundStyle({
                          value: backgroundColor,
                          palettes,
                          parseVariableColor: (v: VariableADTvalue) => {
                              const field = parseBackgroundColorVariable(v)
                              return field ? fieldConvertValue(field) : ''
                          }
                      })
                    : { backgroundColor: patternStyle.backgroundColor }),
                ...(isCustom
                    ? getBorderStyle(borderColor && { ...borderColor, all: 1 }, palettes)
                    : { border: patternStyle.borderColor ? `1px solid ${patternStyle.borderColor}` : 'none' }),
                ...getCursorStyle(appId, cursor)
            }
        }, [
            appId,
            backgroundColor,
            borderColor,
            cursor,
            fillWay,
            isCustom,
            palettes,
            parseBackgroundColorVariable,
            patternStyle.backgroundColor,
            patternStyle.borderColor,
            styles?.container
        ])

        const iconColorText = isCustom ? applyFillPickerValue4CssColorValue(iconColor?.color, palettes) : patternStyle.color

        const handleClick = useCallback(
            (ev: React.MouseEvent<HTMLButtonElement>) => {
                onClick?.(ev)
                if (loading) {
                    return
                }
                onTriggerEvent?.(title)
            },
            [loading, onClick, onTriggerEvent, title]
        )

        const handleMouseOver = useCallback(
            (ev: React.MouseEvent<HTMLButtonElement>) => {
                if (loading) {
                    return
                }
                onMouseOver?.(ev)
            },
            [loading, onMouseOver]
        )

        return (
            <SC.Container
                type={isSubmit ? 'submit' : 'button'}
                size={size}
                radius={`${radius}px`}
                style={style}
                triggerEvents={triggerEvents}
                onClick={handleClick}
                onMouseOver={handleMouseOver}
                disabled={disabled}
                ref={ref}
                {...rest}
            >
                <SC.Wrapper style={styles?.wrapper}>
                    {loading ? (
                        <SC.Icon
                            style={styles?.icon}
                            order={0}
                            type="Spinner"
                            loading={loading}
                            size={buttonIconSizeMap.get(size)}
                            color={iconColorText}
                        />
                    ) : (
                        showIcon &&
                        icon && (
                            <SC.Icon
                                style={styles?.icon}
                                order={iconPosition === 'left' ? 0 : 1}
                                type={icon}
                                size={buttonIconSizeMap.get(size)}
                                color={iconColorText}
                            />
                        )
                    )}

                    {showName && (
                        <TooltipText
                            title={title}
                            render={ref => (
                                <SC.Text
                                    size={buttonTextSizeMap.get(size)}
                                    style={{
                                        ...styles?.title,
                                        ...(isCustom ? getTextColorStyle(color, palettes) : { color: patternStyle.color })
                                    }}
                                >
                                    {title}
                                </SC.Text>
                            )}
                        />
                    )}
                </SC.Wrapper>
            </SC.Container>
        )
    }
)
